import React, {useMemo, useState} from 'react';
import {AuditLog, auditLogStore} from '../../../redux/entities/auditlog';
import StyledMantineTable, { MRT_ColumnDef } from '../../../components/util/StyledMantineTable/StyledMantineTable';
import { Drawer, Paper, Text, Group, ScrollArea, Box, Accordion, Badge } from '@mantine/core';
import gridStyles from '../../GridStyles.module.scss';
import { parseISO } from 'date-fns';
import {bindActionCreators, Dispatch} from 'redux';
import {CommonState} from "../../../redux";
import {userStore, User} from '../../../redux/entities/user';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { camelCaseToTitleCase } from '../../../util/string';
import {CenteredSpinner} from "../../../components/util/widgets/CenteredSpinner/CenteredSpinner";
import {itemStore} from "../../../redux/entities/inventory/item";
import {connect} from "react-redux";
import {
  findItemIdInSection,
  formatDisplayString,
  formatVal,
  getDisplayLabelForItem,
  isContainer,
  isEmptyValue,
  isSensitiveField,
  omitIdFields,
  parseJsonSafe,
  shouldOmitInDetailsKey,
  shouldShowNew,
  tryGetOrderItemTitle
} from './auditLogViewerUtils';

export type AuditLogViewerProps = {
  opened: boolean;
  onClose: () => void;
  auditLogs: AuditLog[];
  loading?: boolean;
  errorMessage?: string;
};

type Props = AuditLogViewerProps & ReturnType<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

const AuditLogViewer = (props: Props) => {
  const { opened, onClose, auditLogs, usersById, getUserById, itemsForLogs, loading, errorMessage } = props;
  const [selected, setSelected] = useState<AuditLog | null>(null);

  // ---------- Shared helpers imported from utils (readability + DRY) ----------
  // Primitive diff renderer used in multiple places
  const renderPrimitiveDiffInline = (oldVal: any, newVal: any) => {
    const hadOld = !isEmptyValue(oldVal);
    const oldTxt = formatVal(oldVal);
    const newTxt = formatVal(newVal);
    return hadOld ? (
      <>
        {oldTxt}
        <FontAwesomeIcon icon="caret-right" style={{ margin: '0 6px' }} />
        {newTxt || '(cleared)'}
      </>
    ) : (
      <>{newTxt}</>
    );
  };

  const getUserName = React.useCallback((id?: string | null) => {
    if (!id) return '';
    // Prefer direct dictionary lookup to avoid stale memoization from selector.getById
    const u = usersById?.[id as any] as User | undefined;
    if (u && u.name) return u.name;
    // Fallback 1: try case-insensitive match by scanning dictionary values
    if (usersById) {
      const lower = String(id).toLowerCase();
      for (const candidate of Object.values(usersById) as User[]) {
        if (String(candidate?.id).toLowerCase() === lower) {
          return candidate?.name || id;
        }
      }
    }
    // Fallback: try provided getUserById function if available
    try {
      const bySel = getUserById?.(id as any) as User | undefined;
      return bySel?.name || id;
    } catch {
      return id || '';
    }
  }, [usersById, getUserById]);

  // Loading is managed by the parent Loader; keep only local selection resets

  // Clear selection when the list changes or viewer is closed
  React.useEffect(() => {
    if (!opened) setSelected(null);
  }, [opened]);

  // Filter out audit logs containing sensitive fields for users
  const filteredAuditLogs = useMemo(() => {
    return auditLogs.filter(log => {
      // If it's a specific property change, check if it's sensitive
      if (log.propertyName && isSensitiveField(log.propertyName)) {
        return false;
      }
      return true;
    });
  }, [auditLogs]);

  const columns = React.useMemo<MRT_ColumnDef<AuditLog>[]>(() => [
    { id: 'action', header: 'Action', accessorKey: 'action', enableSorting: true },
    {
      id: 'userId',
      header: 'User',
      accessorKey: 'userId',
      enableSorting: true,
      Cell: ({ cell }) => {
        const id = cell.getValue<string | null | undefined>();
        return getUserName(id ?? undefined);
      }
    },
    {
      id: 'changedAt',
      header: 'Changed At',
      accessorKey: 'changedAt',
      enableSorting: true,
      Cell: ({ cell }) => {
        const v = cell.getValue<string>();
        if (!v) return '';
        try {
          const d = parseISO(v);
          return d.toLocaleString();
        } catch { return v; }
      }
    },
    // Keep old/new columns defined for searchability, but we'll hide them via initialState
    // and remove them from the Show/Hide Columns menu so they cannot be re-enabled by users.
    { id: 'oldValue', header: 'Old', accessorKey: 'oldValue', enableGlobalFilter: true, enableColumnFilter: false, enableSorting: false, enableHiding: false },
    { id: 'newValue', header: 'New', accessorKey: 'newValue', enableGlobalFilter: true, enableColumnFilter: false, enableSorting: false, enableHiding: false },
  ], [getUserName]);

  // Utility decision helpers are imported from utils

  // Render a nested Accordion representation of a JSON value (single value, no diff)
  const JsonNestedAccordion: React.FC<{ value: any; path?: string; level?: number }> = ({ value, path = 'root', level = 0 }) => {
    const isObject = value && typeof value === 'object' && !Array.isArray(value);
    const isArray = Array.isArray(value);

    if (!isObject && !isArray) {
      // primitive
      return (
        <Text size="sm" style={{ wordBreak: 'break-word' }}>{typeof value === 'string' ? formatDisplayString(value) : String(value)}</Text>
      );
    }

    // Build entries with separate internal key (for path/uniqueness) and user-facing display label
    const entries: { key: string; display: string; value: any }[] = isArray
      ? (value as any[]).map((v, i) => ({ key: String(i), display: getDisplayLabelForItem(v, i), value: v }))
      : Object.entries(value as Record<string, any>).map(([k, v]) => ({ key: k, display: camelCaseToTitleCase(k), value: v }));

    if (entries.length === 0) {
      return <Text size="sm" color="dimmed">(empty)</Text>;
    }

    return (
      <Accordion multiple chevronPosition="left" variant="separated">
        {entries.map(({ key, display, value: v }) => {
          const keyPath = `${path}.${key}`;
          const childIsContainer = isContainer(v);
          // If this looks like an Order Item block, scan the entire section to find an ItemId
          const rawId = v && typeof v === 'object' ? findItemIdInSection(v) : undefined;
          const override = tryGetOrderItemTitle(display, rawId, itemsForLogs);
          const titleToShow = override || display;
          return (
            <Accordion.Item key={keyPath} value={keyPath}>
              <Accordion.Control>
                <Group spacing="xs" noWrap>
                  <Text fw={600} size="sm">{formatDisplayString(titleToShow)}</Text>
                  {!childIsContainer && (
                    <Text size="sm" color="dimmed" truncate>
                      : {typeof v === 'string' ? formatDisplayString(v) : String(v)}
                    </Text>
                  )}
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                {childIsContainer ? (
                  <JsonNestedAccordion value={v} path={keyPath} level={level + 1} />
                ) : (
                  <Text size="sm" style={{ wordBreak: 'break-word' }}>{typeof v === 'string' ? formatDisplayString(v) : String(v)}</Text>
                )}
              </Accordion.Panel>
            </Accordion.Item>
          );
        })}
      </Accordion>
    );
  };

  // Render a nested Accordion representation comparing new vs old values
  const JsonNestedDiffAccordion: React.FC<{ newValue: any; oldValue: any; path?: string; level?: number }> = ({ newValue, oldValue, path = 'root', level = 0 }) => {
    const newIsObject = newValue && typeof newValue === 'object' && !Array.isArray(newValue);
    const newIsArray = Array.isArray(newValue);
    const oldIsObject = oldValue && typeof oldValue === 'object' && !Array.isArray(oldValue);
    const oldIsArray = Array.isArray(oldValue);

    // Primitive case
    if (!newIsObject && !newIsArray) {
      if (!shouldShowNew(newValue, oldValue)) return null;
      return (
        <Text size="sm" style={{ wordBreak: 'break-word' }}>
          {renderPrimitiveDiffInline(oldValue, newValue)}
        </Text>
      );
    }

    // Build entries from object or array, pairing with counterpart old values
    const entries: { key: string; display: string; newV: any; oldV: any }[] = newIsArray
      ? (newValue as any[])
          .map((v, i) => ({
            key: String(i),
            display: getDisplayLabelForItem(v, i),
            newV: v,
            oldV: oldIsArray ? (oldValue as any[])[i] : undefined,
          }))
          // If the accordion label contains "Id" (case sensitive), omit the whole item
          .filter((e) => !e.display.includes('Id'))
      : Object.entries(newValue as Record<string, any>)
          .filter(([k]) => !shouldOmitInDetailsKey(k))
          .map(([k, v]) => ({
            key: k,
            display: camelCaseToTitleCase(k),
            newV: v,
            oldV: oldIsObject ? (oldValue as any)[k] : undefined,
          }));

    // Filter entries based on shouldShowNew
    const filtered = entries.filter(e => shouldShowNew(e.newV, e.oldV));

    if (filtered.length === 0) {
      // If nothing to show but the old had content, show a cleared indicator
      if (isEmptyValue(newValue) && !isEmptyValue(oldValue)) {
        return <Text size="sm" color="dimmed">(cleared)</Text>;
      }
      return <Text size="sm" color="dimmed">(empty)</Text>;
    }

    return (
      <Accordion multiple chevronPosition="left" variant="separated">
        {filtered.map(({ key, display, newV, oldV }) => {
          const keyPath = `${path}.${key}`;
          const childNewIsContainer = isContainer(newV);
          // For Order Item sections, scan the entire section (newV/oldV) to locate an ItemId
          const sourceForId = (newV ?? oldV);
          const rawId = sourceForId && typeof sourceForId === 'object' ? findItemIdInSection(sourceForId) : undefined;
          const override = tryGetOrderItemTitle(display, rawId, itemsForLogs);
          const titleToShow = override || display;
          const content = childNewIsContainer ? (
            <JsonNestedDiffAccordion newValue={newV} oldValue={oldV} path={keyPath} level={(level ?? 0) + 1} />
          ) : (
            <Text size="sm" style={{ wordBreak: 'break-word' }}>
              {renderPrimitiveDiffInline(oldV, newV)}
            </Text>
          );
          return (
            <Accordion.Item key={keyPath} value={keyPath}>
              <Accordion.Control>
                <Group spacing="xs" noWrap>
                  <Text fw={600} size="sm">{formatDisplayString(titleToShow)}</Text>
                  {!childNewIsContainer && (
                    <Text size="sm" color="dimmed" truncate>
                      : {renderPrimitiveDiffInline(oldV, newV)}
                    </Text>
                  )}
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                {content}
              </Accordion.Panel>
            </Accordion.Item>
          );
        })}
      </Accordion>
    );
  };

  const renderNestedAccordionFromJson = (raw?: string | null) => {
    if (!raw) return <Text size="sm" color="dimmed">No data</Text>;
    const parsed = parseJsonSafe(raw);
    if (typeof parsed === 'object') return <JsonNestedAccordion value={parsed} />;
    // If not JSON, just render as text
    return (
      <Text size="sm" style={{ wordBreak: 'break-word' }}>
        {typeof raw === 'string' ? formatDisplayString(raw) : String(raw)}
      </Text>
    );
  };


  // Render Old Values panel: show all old values except fields containing "Id"
  const renderOldValuesFiltered = (raw?: string | null) => {
    if (!raw) return <Text size="sm" color="dimmed">No data</Text>;
    const parsed = parseJsonSafe(raw);
    if (typeof parsed !== 'object') return <Text size="sm" style={{ wordBreak: 'break-word' }}>{raw}</Text>;
    const filtered = omitIdFields(parsed);
    const empty = isEmptyValue(filtered);
    if (empty) return <Text size="sm" color="dimmed">(empty)</Text>;
    return <JsonNestedAccordion value={filtered} />;

  };

  const renderNestedDiffFromJson = (newRaw?: string | null, oldRaw?: string | null) => {
    const newParsed = parseJsonSafe(newRaw);
    const oldParsed = parseJsonSafe(oldRaw);
    // If both are primitives or non-objects, apply simple rule
    if (typeof newParsed !== 'object' || newParsed === null) {
      if (!shouldShowNew(newParsed, oldParsed)) return <Text size="sm" color="dimmed">(empty)</Text>;
      return (
        <Text size="sm" style={{ wordBreak: 'break-word' }}>
          {renderPrimitiveDiffInline(oldParsed, newParsed)}
        </Text>
      );
    }
    return <JsonNestedDiffAccordion newValue={newParsed} oldValue={oldParsed} />;
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={<Text fw={700}>Audit Logs</Text>}
      position="right"
      size="85%"
      withinPortal
      overlayProps={{ opacity: 0.35, blur: 1 }}
      styles={{
        content: { display: 'flex', flexDirection: 'column', maxWidth: '100rem' },
        body: { display: 'flex', flexDirection: 'column', height: '100%', paddingTop: 0 }
      }}
      transitionProps={{duration: 500}}
    >
      {errorMessage && <Text color="red" size="sm" style={{ marginBottom: 8 }}>{errorMessage}</Text>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>
        <div style={{ display: 'flex', gap: 12, height: '100%', minHeight: 0 }}>
          <Paper withBorder shadow="sm" radius="md" p="sm" style={{ flex: 2, minWidth: 0, minHeight: 0 }}>
            <StyledMantineTable
              columns={columns}
              data={filteredAuditLogs}
              initialState={{ pagination: { pageSize: 25 }, columnVisibility: { oldValue: false, newValue: false } }}
              enableExpanding
              themeVariant="audit"
              // Do not show old/new values as table columns (kept hidden for global search)
              renderDetailPanel={({ row }: any) => {
                const item = row.original as AuditLog;
                return (
                  <Box p="sm" sx={{ width: '100%' }}>
                    <Group spacing="xs" mb={6}>
                      <Badge color="blue" variant="light">{item.action}</Badge>
                      {item.userId && (
                        <Badge color="teal" variant="light">User: {getUserName(item.userId)}</Badge>
                      )}
                    </Group>
                    <Text size="xs" color="dimmed" mb={8}>Changed: {(() => { try { return item.changedAt ? parseISO(item.changedAt).toLocaleString() : ''; } catch { return item.changedAt; } })()}</Text>
                    <Accordion multiple={true} variant="contained" defaultValue={["new", "old"]}>
                      <Accordion.Item value="new">
                        <Accordion.Control>Details</Accordion.Control>
                        <Accordion.Panel>
                          {renderNestedDiffFromJson(item.newValue, item.oldValue)}
                        </Accordion.Panel>
                      </Accordion.Item>
                    </Accordion>
                  </Box>
                );
              }}
              mantineTableProps={{
                striped: true,
                highlightOnHover: true,
                withColumnBorders: true,
                className: gridStyles['table-auto'],
              }}
              mantineTableBodyRowProps={({ row }: any) => {
                const rowLog = row.original as AuditLog;
                const isSelected = selected?.id === rowLog.id;
                return {
                  onClick: () => setSelected(rowLog),
                  sx: {
                    cursor: 'pointer',
                    backgroundColor: isSelected ? 'var(--mantine-color-blue-light)' : undefined,
                  },
                };
              }}
            />
          </Paper>
        </div>
        {loading && <CenteredSpinner/>}
      </div>
    </Drawer>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) => ({actions: bindActionCreators({}, dispatch)});
const mapStateToProps = (state: CommonState) => ({
  entityAuditLogs: auditLogStore.selectors.getEntityAuditLogs(state),
  usersById: userStore.selectors.getItems(state),
  getUserById: userStore.selectors.getById(state),
  getItemById: itemStore.selectors.getById(state),
  itemsForLogs: itemStore.selectors.getItemsForViewedLogsAsArray(state)
});

export default connect(mapStateToProps, mapDispatchToProps)(AuditLogViewer);
